import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import {
  X, Building2, Users, Briefcase, FileText, CreditCard, Save, Upload, Plus, Trash2,
  Eye, EyeOff, Copy, Check, Calendar, MapPin, Hash, Palette, ChevronDown, Search, Edit2,
  Download, RefreshCw, Mail, Star, AlertTriangle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { CLIENT_COLOR_PALETTE, getLighterColor } from '../lib/clientColors';
import { formatDate } from '../lib/dateUtils';
import ClientAgreementsTab, { ClientAgreementsSummary, useClientAgreements } from './accounting/contracts/ClientAgreements';

// Indian States List
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  // Union Territories
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

// Employee Search Dropdown Component
function EmployeeSearchDropdown({ employees, value, onChange, placeholder = "Select POC..." }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEmployees = employees.filter(emp => {
    const name = emp.personal?.full_name || emp.work_email || '';
    const email = emp.work_email || '';
    const search = searchTerm.toLowerCase();
    return name.toLowerCase().includes(search) || email.toLowerCase().includes(search);
  });

  const selectedEmployee = employees.find(emp => emp._id === value);
  const displayValue = selectedEmployee 
    ? (selectedEmployee.personal?.full_name || selectedEmployee.work_email)
    : '';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 text-sm text-left border rounded-xl bg-white flex items-center justify-between ${
          isOpen ? 'ring-2 ring-pink-500 border-pink-500' : 'border-white/40'
        }`}
      >
        <span className={displayValue ? 'text-[#1a1a2e]' : 'text-neutral-400'}>
          {displayValue || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 glass-card rounded-xl shadow-xl">
          <div className="p-2 border-b border-white/30">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(''); setIsOpen(false); setSearchTerm(''); }}
              className="w-full px-3 py-2 text-left text-sm text-neutral-500 hover:bg-white/60 flex items-center gap-2 border-b"
            >
              <X className="w-4 h-4" /> Clear selection
            </button>
            {filteredEmployees.map((emp) => (
              <button
                key={emp._id}
                type="button"
                onClick={() => { onChange(emp._id); setIsOpen(false); setSearchTerm(''); }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-pink-50 ${value === emp._id ? 'bg-pink-50' : ''}`}
              >
                <p className="font-medium text-[#1a1a2e]">{emp.personal?.full_name || emp.work_email}</p>
                <p className="text-xs text-neutral-500">{emp.designation || 'No designation'}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Password Field with Toggle
function PasswordField({ value, onChange, placeholder }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative flex items-center gap-1">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="flex-1 px-3 py-2 text-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
      />
      <button type="button" onClick={() => setVisible(!visible)} className="p-2 text-neutral-400 hover:text-neutral-600">
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
      <button type="button" onClick={handleCopy} className="p-2 text-neutral-400 hover:text-neutral-600">
        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

// Human-friendly labels for backend field names — used in the error banner.
const FIELD_LABELS = {
  name: 'Client name',
  retainer: 'Retainer amount',
  currency: 'Currency',
  category: 'Category',
  color: 'Color',
  company_name: 'Legal company name',
  company_address: 'Company address',
  company_state: 'State',
  company_pin_code: 'Pin code',
  gstin: 'GSTIN',
  pan: 'PAN',
  billing_cycle: 'Billing cycle',
  payment_type: 'Payment type',
  retainer_start_date: 'Retainer start date',
  retainer_end_date: 'Retainer end date',
  departments: 'Departments',
  pocs: 'POCs',
  billing_contacts: 'Billing contacts',
  credentials: 'Credentials',
  logo_file: 'Logo',
  brand_book_file: 'Brand book',
  gst_certificate_file: 'GST certificate',
};

// Map each field to the tab where the user can edit it — for click-to-jump in the banner.
const FIELD_TO_TAB = {
  name: 'overview', retainer: 'overview', currency: 'overview', category: 'overview', color: 'overview',
  retainer_start_date: 'overview', retainer_end_date: 'overview', billing_cycle: 'overview', payment_type: 'overview',
  company_name: 'company', company_address: 'company', company_state: 'company',
  company_pin_code: 'company', gstin: 'company', pan: 'company',
  departments: 'departments', pocs: 'departments',
  billing_contacts: 'billing', logo_file: 'files', brand_book_file: 'files',
  gst_certificate_file: 'files', credentials: 'credentials',
};

const TAB_LABELS = {
  overview: 'Overview',
  company: 'Company Details',
  departments: 'Departments & POCs',
  credentials: 'Credentials',
  billing: 'Billing',
  files: 'Files',
};

const labelFor = (key) => FIELD_LABELS[key] || key;

export default function ClientDetailsModal({ 
  isOpen, 
  onClose, 
  clientId, 
  employees = [], 
  departments = [],
  categories = [],
  onSave,
  onCategoriesUpdate
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const agreements = useClientAgreements(isOpen ? clientId : null);
  const [clientData, setClientData] = useState(null);
  const [formData, setFormData] = useState({});
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [uploadingFile, setUploadingFile] = useState(null);
  // Validation errors from the backend (keyed by field name).
  // Cleared on every save attempt; populated on Pydantic 422.
  const [fieldErrors, setFieldErrors] = useState({});

  // Fetch client details
  useEffect(() => {
    if (isOpen && clientId) {
      fetchClientDetails();
    }
  }, [isOpen, clientId]);

  const fetchClientDetails = async (silent = false) => {
    if (silent !== true) setLoading(true);
    try {
      const response = await adminApi.get(`/api/admin/clients/${clientId}/full-details`);
      const data = response.data;
      setClientData(data);
      setFormData({
        name: data.client.name || '',
        retainer: data.client.retainer || '',
        currency: data.client.currency || 'INR',
        category: data.client.category || '',
        departments: data.client.departments || [],
        pocs: data.client.pocs || {},
        color: data.client.color || '',
        company_name: data.client.company_name || '',
        company_address: data.client.company_address || '',
        company_state: data.client.company_state || '',
        company_pin_code: data.client.company_pin_code || '',
        gstin: data.client.gstin || '',
        pan: data.client.pan || '',
        billing_cycle: data.client.billing_cycle || 'monthly',
        payment_type: data.client.payment_type || 'postpaid',
        retainer_start_date: data.client.retainer_start_date ? String(data.client.retainer_start_date).slice(0, 10) : '',
        retainer_end_date: data.client.retainer_end_date ? String(data.client.retainer_end_date).slice(0, 10) : '',
        credentials: data.client.credentials || [],
        billing_contacts: data.client.billing_contacts || [],
        logo_file: data.client.logo_file || '',
        brand_book_file: data.client.brand_book_file || '',
        gst_certificate_file: data.client.gst_certificate_file || '',
      });
    } catch (error) {
      console.error('Error fetching client details:', error);
      toast.error('Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setFieldErrors({});
    try {
      // Sanitize payload: empty strings on numeric fields → null, so Pydantic
      // can coerce properly instead of failing validation.
      const rawRetainer = formData.retainer;
      let retainerValue = null;
      if (typeof rawRetainer === 'number' && Number.isFinite(rawRetainer)) {
        retainerValue = rawRetainer;
      } else if (typeof rawRetainer === 'string' && rawRetainer.trim() !== '') {
        const n = Number(rawRetainer);
        retainerValue = Number.isFinite(n) ? n : null;
      }
      const payload = { ...formData, retainer: retainerValue };

      await adminApi.put(`/api/admin/clients/${clientId}`, payload);
      toast.success('Client updated successfully');
      if (onSave) onSave();
      if (showNewCategory && newCategory && onCategoriesUpdate) {
        onCategoriesUpdate();
      }
    } catch (error) {
      console.error('Error saving client:', error);
      const detail = error.response?.data?.detail;
      // Pydantic 422 returns an array of {loc, msg, type}. Map every error to
      // its field so we can surface ALL issues at once, not just the first.
      if (Array.isArray(detail) && detail.length > 0) {
        const errs = {};
        detail.forEach((d) => {
          const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : 'form';
          errs[field] = d.msg || 'Invalid value';
        });
        setFieldErrors(errs);
        toast.error(
          `Please fix ${Object.keys(errs).length} field${Object.keys(errs).length === 1 ? '' : 's'}: ` +
          Object.keys(errs).map(labelFor).join(', ')
        );
      } else {
        toast.error(`Failed to save: ${detail || error.message || 'Unknown error'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDepartmentToggle = (deptName) => {
    const isSelected = formData.departments.includes(deptName);
    if (isSelected) {
      const newDepts = formData.departments.filter(d => d !== deptName);
      const newPocs = { ...formData.pocs };
      delete newPocs[deptName];
      setFormData({ ...formData, departments: newDepts, pocs: newPocs });
    } else {
      setFormData({ ...formData, departments: [...formData.departments, deptName] });
    }
  };

  const handlePOCSelect = (department, employeeId) => {
    setFormData({
      ...formData,
      pocs: { ...formData.pocs, [department]: employeeId }
    });
  };

  // Credentials management
  const addCredential = () => {
    setFormData({
      ...formData,
      credentials: [...formData.credentials, { platform: '', username: '', password: '', backup_codes: '', notes: '' }]
    });
  };

  const updateCredential = (index, field, value) => {
    const updated = [...formData.credentials];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, credentials: updated });
  };

  const removeCredential = (index) => {
    setFormData({
      ...formData,
      credentials: formData.credentials.filter((_, i) => i !== index)
    });
  };

  // Billing contacts management
  const addBillingContact = () => {
    const contacts = formData.billing_contacts || [];
    setFormData({
      ...formData,
      billing_contacts: [...contacts, { name: '', email: '', role: 'Accounts', is_primary: contacts.length === 0 }]
    });
  };

  const updateBillingContact = (index, field, value) => {
    const updated = [...(formData.billing_contacts || [])];
    updated[index] = { ...updated[index], [field]: value };
    // If setting is_primary, unset others
    if (field === 'is_primary' && value) {
      updated.forEach((c, i) => { if (i !== index) c.is_primary = false; });
    }
    setFormData({ ...formData, billing_contacts: updated });
  };

  const removeBillingContact = (index) => {
    const updated = (formData.billing_contacts || []).filter((_, i) => i !== index);
    // Ensure at least one primary if contacts exist
    if (updated.length > 0 && !updated.some(c => c.is_primary)) {
      updated[0].is_primary = true;
    }
    setFormData({ ...formData, billing_contacts: updated });
  };

  // File upload
  const handleFileUpload = async (fileType, file) => {
    if (!file) return;
    
    setUploadingFile(fileType);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await adminApi.post(
        `/api/admin/clients/${clientId}/upload-file?file_type=${fileType}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setFormData(prev => ({ ...prev, [`${fileType}_file`]: response.data.file_url }));
      toast.success(`${fileType.replace('_', ' ')} uploaded successfully`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload ${fileType.replace('_', ' ')}`);
    } finally {
      setUploadingFile(null);
    }
  };

  const handleCategorySelect = (value) => {
    if (value === '_new_') {
      setShowNewCategory(true);
    } else {
      setFormData({ ...formData, category: value });
      setShowNewCategory(false);
    }
  };

  const handleNewCategorySubmit = () => {
    if (newCategory.trim()) {
      setFormData({ ...formData, category: newCategory.trim() });
      setShowNewCategory(false);
      setNewCategory('');
    }
  };

  const currencies = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' }
  ];

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] flex flex-col overflow-hidden p-0 gap-0 bg-white" aria-describedby={undefined}>
        <VisuallyHidden><DialogTitle>{formData.name || 'Client Details'}</DialogTitle></VisuallyHidden>
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {formData.color && (
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg"
                  style={{ backgroundColor: formData.color }}
                >
                  {formData.name?.charAt(0) || 'C'}
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold">{formData.name || 'Client Details'}</h2>
                <p className="text-neutral-300 text-sm">{formData.category}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-neutral-400 hover:text-white transition">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Quick Stats */}
          {clientData && (
            <div className="flex gap-6 mt-4 pt-4 border-t border-slate-700">
              <div className="text-center">
                <p className="text-2xl font-bold text-teal-400">{clientData.total_tasks || 0}</p>
                <p className="text-xs text-neutral-400">Total Tasks</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-400">{clientData.department_count || 0}</p>
                <p className="text-xs text-neutral-400">Departments</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-400">{clientData.user_count || 0}</p>
                <p className="text-xs text-neutral-400">Users</p>
              </div>
              {formData.retainer > 0 && (
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">
                    {currencies.find(c => c.code === formData.currency)?.symbol || '₹'}
                    {parseFloat(formData.retainer).toLocaleString()}
                  </p>
                  <p className="text-xs text-neutral-400">Retainer</p>
                </div>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <RefreshCw className="w-8 h-8 animate-spin text-pink-500" />
          </div>
        ) : (
          <>
            {/* Validation error banner — visible across every tab until cleared */}
            {Object.keys(fieldErrors).length > 0 && (
              <div
                className="mx-6 mt-3 mb-1 rounded-xl border border-rose-200 bg-rose-50/80 p-4 flex items-start gap-3"
                data-testid="client-modal-error-banner"
                role="alert"
              >
                <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-rose-800">
                    Fix {Object.keys(fieldErrors).length} field{Object.keys(fieldErrors).length === 1 ? '' : 's'} before saving:
                  </div>
                  <ul className="mt-1.5 space-y-1 text-xs text-rose-700">
                    {Object.entries(fieldErrors).map(([field, msg]) => {
                      const fieldTab = FIELD_TO_TAB[field];
                      return (
                        <li key={field} className="flex items-start gap-2">
                          <span className="text-rose-400 mt-0.5">•</span>
                          <span className="flex-1">
                            <button
                              type="button"
                              className="font-medium underline-offset-2 hover:underline"
                              onClick={() => fieldTab && setActiveTab(fieldTab)}
                            >
                              {labelFor(field)}
                            </button>
                            <span className="text-rose-600"> — {msg}</span>
                            {fieldTab && (
                              <span className="ml-1 text-[10.5px] text-rose-500/80">
                                (in {TAB_LABELS[fieldTab] || fieldTab} tab)
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <button
                  type="button"
                  onClick={() => setFieldErrors({})}
                  className="p-1 rounded-md hover:bg-rose-100 text-rose-500 flex-shrink-0"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              <TabsList className="px-6 pt-4 bg-white/40 border-b flex-shrink-0">
                <TabsTrigger value="overview" className="data-[state=active]:bg-white">
                  <Building2 className="w-4 h-4 mr-2" /> Overview
                </TabsTrigger>
                <TabsTrigger value="company" className="data-[state=active]:bg-white">
                  <FileText className="w-4 h-4 mr-2" /> Company Details
                </TabsTrigger>
                <TabsTrigger value="departments" className="data-[state=active]:bg-white">
                  <Users className="w-4 h-4 mr-2" /> Departments & POCs
                </TabsTrigger>
                <TabsTrigger value="credentials" className="data-[state=active]:bg-white">
                  <CreditCard className="w-4 h-4 mr-2" /> Credentials
                </TabsTrigger>
                <TabsTrigger value="billing_contacts" className="data-[state=active]:bg-white">
                  <Mail className="w-4 h-4 mr-2" /> Billing Contacts
                </TabsTrigger>
                <TabsTrigger value="files" className="data-[state=active]:bg-white">
                  <Upload className="w-4 h-4 mr-2" /> Files
                </TabsTrigger>
                {agreements.allowed && (
                  <TabsTrigger value="agreements" className="data-[state=active]:bg-white" data-testid="client-tab-agreements">
                    <FileText className="w-4 h-4 mr-2" /> Contracts
                  </TabsTrigger>
                )}
              </TabsList>

              <div className="flex-1 overflow-y-auto p-6 min-h-0">
                {agreements.allowed && (
                  <TabsContent value="agreements" className="mt-0">
                    <ClientAgreementsTab clientId={clientId} data={agreements.data} settings={agreements.settings} reload={agreements.reload} onClientUpdated={() => fetchClientDetails(true)} />
                  </TabsContent>
                )}
                {/* Overview Tab */}
                <TabsContent value="overview" className="mt-0 space-y-6">
                  {agreements.allowed && <ClientAgreementsSummary data={agreements.data} onViewAll={() => setActiveTab('agreements')} />}
                  <div className="grid grid-cols-2 gap-6">
                    {/* Client Name */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">Client Name *</label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter client name"
                        data-testid="client-name-input"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">Category *</label>
                      {!showNewCategory ? (
                        <Select value={formData.category} onValueChange={handleCategorySelect}>
                          <SelectTrigger data-testid="client-category-select">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                            <SelectItem value="_new_">+ Create New Category</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            placeholder="New category name"
                            autoFocus
                          />
                          <Button onClick={handleNewCategorySubmit} size="sm">Add</Button>
                          <Button variant="outline" size="sm" onClick={() => setShowNewCategory(false)}>Cancel</Button>
                        </div>
                      )}
                    </div>

                    {/* Retainer */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">Retainer Amount</label>
                      <div className="flex gap-2">
                        <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {currencies.map((curr) => (
                              <SelectItem key={curr.code} value={curr.code}>{curr.symbol} {curr.code}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          value={formData.retainer}
                          onChange={(e) => setFormData({ ...formData, retainer: e.target.value })}
                          placeholder="Enter amount"
                          className="flex-1"
                          data-testid="client-retainer-input"
                        />
                      </div>
                    </div>

                    {/* Client Color */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">Client Color</label>
                      <div className="flex items-center gap-2 flex-wrap">
                        {CLIENT_COLOR_PALETTE.slice(0, 12).map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setFormData({ ...formData, color })}
                            className={`w-8 h-8 rounded-xl transition-all ${
                              formData.color === color ? 'ring-2 ring-offset-2 ring-gray-900 scale-110' : 'hover:scale-105'
                            }`}
                            style={{ backgroundColor: color }}
                            data-testid={`color-${color}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Billing Settings */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-[#1a1a2e] mb-4">Billing Settings</h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-neutral-700 mb-2">Billing Cycle</label>
                        <Select value={formData.billing_cycle} onValueChange={(v) => setFormData({ ...formData, billing_cycle: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="yearly">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-neutral-700 mb-2">Payment Type</label>
                        <Select value={formData.payment_type} onValueChange={(v) => setFormData({ ...formData, payment_type: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="advance">Advance</SelectItem>
                            <SelectItem value="postpaid">Postpaid</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-neutral-700 mb-2">Retainer Start Date</label>
                        <Input
                          type="date"
                          value={formData.retainer_start_date}
                          onChange={(e) => setFormData({ ...formData, retainer_start_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-neutral-700 mb-2">Retainer End Date</label>
                        <Input
                          type="date"
                          value={formData.retainer_end_date}
                          onChange={(e) => setFormData({ ...formData, retainer_end_date: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Company Details Tab */}
                <TabsContent value="company" className="mt-0 space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <p className="text-sm text-blue-800">
                      <strong>Important:</strong> Company state affects GST calculation. Karnataka = CGST + SGST, Other states = IGST
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">Legal Company Name</label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        placeholder="Enter legal company name"
                        data-testid="company-name-input"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">Company Address</label>
                      <Textarea
                        value={formData.company_address}
                        onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                        placeholder="Enter full address"
                        rows={3}
                        data-testid="company-address-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">Company State *</label>
                      <Select value={formData.company_state} onValueChange={(v) => setFormData({ ...formData, company_state: v })}>
                        <SelectTrigger data-testid="company-state-select">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {INDIAN_STATES.map((state) => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">Pin Code</label>
                      <Input
                        value={formData.company_pin_code}
                        onChange={(e) => setFormData({ ...formData, company_pin_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                        placeholder="6-digit pin code"
                        maxLength={6}
                        data-testid="company-pincode-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">GSTIN</label>
                      <Input
                        value={formData.gstin}
                        onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
                        placeholder="e.g., 29AAFCH4136C1ZT"
                        maxLength={15}
                        data-testid="company-gstin-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">PAN</label>
                      <Input
                        value={formData.pan}
                        onChange={(e) => setFormData({ ...formData, pan: e.target.value.toUpperCase() })}
                        placeholder="e.g., AAAAA0000A"
                        maxLength={10}
                        data-testid="company-pan-input"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Departments & POCs Tab */}
                <TabsContent value="departments" className="mt-0 space-y-4">
                  <p className="text-sm text-neutral-600 mb-4">
                    Select departments that work with this client and assign a default Point of Contact for each.
                  </p>

                  <div className="space-y-3 border rounded-xl p-4 max-h-96 overflow-y-auto">
                    {departments.length === 0 ? (
                      <p className="text-neutral-500 text-center py-8">No departments available</p>
                    ) : (
                      departments.map((dept) => {
                        // Filter employees by department name (departments are now stored as names)
                        const deptEmployees = employees.filter(emp => {
                          const empDepts = emp.departments || emp.employment?.departments || [];
                          return empDepts.includes(dept.name);
                        });

                        return (
                          <div 
                            key={dept._id || dept.name} 
                            className={`flex items-center gap-4 p-3 rounded-xl border transition ${
                              formData.departments.includes(dept.name) ? 'bg-pink-50 border-pink-200' : 'bg-white/40 border-white/40'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.departments.includes(dept.name)}
                              onChange={() => handleDepartmentToggle(dept.name)}
                              className="w-5 h-5 text-pink-600 focus:ring-pink-500 rounded"
                              data-testid={`dept-checkbox-${dept.name}`}
                            />
                            <span className="flex-1 font-medium text-[#1a1a2e]">{dept.name}</span>
                            {formData.departments.includes(dept.name) && (
                              <div className="w-64">
                                <EmployeeSearchDropdown
                                  employees={deptEmployees.length > 0 ? deptEmployees : employees}
                                  value={formData.pocs[dept.name] || ''}
                                  onChange={(empId) => handlePOCSelect(dept.name, empId)}
                                  placeholder="Select POC..."
                                />
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </TabsContent>

                {/* Credentials Tab */}
                <TabsContent value="credentials" className="mt-0 space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-[#1a1a2e]">Client Credentials</h3>
                      <p className="text-sm text-neutral-500">Store login credentials for client platforms securely.</p>
                    </div>
                    <Button onClick={addCredential} size="sm" className="bg-pink-600 hover:bg-pink-700">
                      <Plus className="w-4 h-4 mr-1" /> Add Credential
                    </Button>
                  </div>

                  {formData.credentials.length === 0 ? (
                    <div className="text-center py-12 bg-white/40 rounded-xl border-2 border-dashed">
                      <CreditCard className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                      <p className="text-neutral-500 mb-4">No credentials added yet</p>
                      <Button onClick={addCredential} variant="outline" size="sm">
                        <Plus className="w-4 h-4 mr-1" /> Add First Credential
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.credentials.map((cred, index) => (
                        <div key={index} className="bg-white/40 rounded-xl p-4 border">
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-semibold text-neutral-700">Credential #{index + 1}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeCredential(index)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-neutral-600 mb-1">Platform</label>
                              <Input
                                value={cred.platform}
                                onChange={(e) => updateCredential(index, 'platform', e.target.value)}
                                placeholder="e.g., Instagram, Google Ads"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-neutral-600 mb-1">Username / Email</label>
                              <Input
                                value={cred.username}
                                onChange={(e) => updateCredential(index, 'username', e.target.value)}
                                placeholder="Username or email"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-neutral-600 mb-1">Password</label>
                              <PasswordField
                                value={cred.password}
                                onChange={(e) => updateCredential(index, 'password', e.target.value)}
                                placeholder="Password"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-neutral-600 mb-1">Backup Codes / Authenticator</label>
                              <Input
                                value={cred.backup_codes}
                                onChange={(e) => updateCredential(index, 'backup_codes', e.target.value)}
                                placeholder="2FA codes or notes"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-neutral-600 mb-1">Notes</label>
                              <Textarea
                                value={cred.notes}
                                onChange={(e) => updateCredential(index, 'notes', e.target.value)}
                                placeholder="Additional notes..."
                                rows={2}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Billing Contacts Tab */}
                <TabsContent value="billing_contacts" className="mt-0 space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-neutral-600">
                      Manage email contacts for invoices and billing communications.
                    </p>
                    <Button variant="outline" size="sm" onClick={addBillingContact} data-testid="add-billing-contact-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Contact
                    </Button>
                  </div>

                  {(!formData.billing_contacts || formData.billing_contacts.length === 0) ? (
                    <div className="text-center py-10 bg-white/40 rounded-xl border border-dashed">
                      <Mail className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                      <p className="text-sm text-neutral-500">No billing contacts yet</p>
                      <p className="text-xs text-neutral-400 mt-1">Add contacts to auto-fill email recipients when sending invoices</p>
                      <Button variant="outline" size="sm" className="mt-3" onClick={addBillingContact}>
                        <Plus className="w-4 h-4 mr-1" /> Add First Contact
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.billing_contacts.map((contact, idx) => (
                        <div key={idx} className={`border rounded-xl p-4 ${contact.is_primary ? 'border-indigo-200 bg-indigo-50/30' : 'bg-white'}`} data-testid={`billing-contact-${idx}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateBillingContact(idx, 'is_primary', true)}
                                className={`p-0.5 rounded ${contact.is_primary ? 'text-amber-500' : 'text-neutral-300 hover:text-amber-400'}`}
                                title={contact.is_primary ? 'Primary contact' : 'Set as primary'}
                                data-testid={`primary-toggle-${idx}`}
                              >
                                <Star className={`w-4 h-4 ${contact.is_primary ? 'fill-amber-500' : ''}`} />
                              </button>
                              {contact.is_primary && (
                                <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded uppercase tracking-wide">Primary</span>
                              )}
                            </div>
                            <button onClick={() => removeBillingContact(idx)} className="p-1 hover:bg-red-50 rounded text-neutral-400 hover:text-red-500" data-testid={`remove-contact-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="text-xs font-medium text-neutral-500 mb-1 block">Name</label>
                              <Input
                                value={contact.name || ''}
                                onChange={(e) => updateBillingContact(idx, 'name', e.target.value)}
                                placeholder="Contact name"
                                className="text-sm"
                                data-testid={`contact-name-${idx}`}
                              />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-neutral-500 mb-1 block">Email</label>
                              <Input
                                type="email"
                                value={contact.email || ''}
                                onChange={(e) => updateBillingContact(idx, 'email', e.target.value)}
                                placeholder="email@company.com"
                                className="text-sm"
                                data-testid={`contact-email-${idx}`}
                              />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-neutral-500 mb-1 block">Role</label>
                              <select
                                value={contact.role || 'Accounts'}
                                onChange={(e) => updateBillingContact(idx, 'role', e.target.value)}
                                className="w-full h-9 px-3 rounded-xl border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                data-testid={`contact-role-${idx}`}
                              >
                                <option value="Accounts">Accounts</option>
                                <option value="Finance">Finance</option>
                                <option value="Manager">Manager</option>
                                <option value="Director">Director</option>
                                <option value="Admin">Admin</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Files Tab */}
                <TabsContent value="files" className="mt-0 space-y-6">
                  <p className="text-sm text-neutral-600 mb-4">
                    Upload client brand assets and important documents.
                  </p>

                  <div className="grid grid-cols-3 gap-6">
                    {/* Logo */}
                    <FileUploadCard
                      title="Logo Files"
                      description="Upload client logo (PNG, JPG, PDF)"
                      fileUrl={formData.logo_file}
                      fileType="logo"
                      uploading={uploadingFile === 'logo'}
                      onUpload={(file) => handleFileUpload('logo', file)}
                    />

                    {/* Brand Book */}
                    <FileUploadCard
                      title="Brand Book"
                      description="Brand guidelines document"
                      fileUrl={formData.brand_book_file}
                      fileType="brand_book"
                      uploading={uploadingFile === 'brand_book'}
                      onUpload={(file) => handleFileUpload('brand_book', file)}
                    />

                    {/* GST Certificate */}
                    <FileUploadCard
                      title="GST Certificate"
                      description="GST registration certificate"
                      fileUrl={formData.gst_certificate_file}
                      fileType="gst_certificate"
                      uploading={uploadingFile === 'gst_certificate'}
                      onUpload={(file) => handleFileUpload('gst_certificate', file)}
                    />
                  </div>
                </TabsContent>
              </div>
            </Tabs>

            {/* Footer */}
            <div className="bg-white/40 border-t px-6 py-4 flex items-center justify-between sticky bottom-0">
              <p className="text-sm text-neutral-500">
                Last updated: {clientData?.client?.updated_at ? formatDate(clientData.client.updated_at) : 'Never'}
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving || !formData.name}
                  className="bg-pink-600 hover:bg-pink-700"
                  data-testid="save-client-btn"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// File Upload Card Component
function FileUploadCard({ title, description, fileUrl, fileType, uploading, onUpload }) {
  const inputRef = useRef(null);

  return (
    <div className="border-2 border-dashed rounded-xl p-6 text-center hover:border-pink-300 transition">
      <Upload className={`w-10 h-10 mx-auto mb-3 ${fileUrl ? 'text-green-500' : 'text-neutral-300'}`} />
      <h4 className="font-semibold text-[#1a1a2e] mb-1">{title}</h4>
      <p className="text-xs text-neutral-500 mb-4">{description}</p>
      
      {fileUrl ? (
        <div className="space-y-2">
          <p className="text-xs text-green-600 truncate">File uploaded</p>
          <div className="flex gap-2 justify-center">
            <a 
              href={fileUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-xs bg-neutral-100 text-neutral-700 rounded hover:bg-neutral-200 flex items-center gap-1"
            >
              <Eye className="w-3 h-3" /> View
            </a>
            <button
              onClick={() => inputRef.current?.click()}
              className="px-3 py-1.5 text-xs bg-pink-100 text-pink-700 rounded hover:bg-pink-200 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> Replace
            </button>
          </div>
        </div>
      ) : (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      )}
      
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.zip"
        onChange={(e) => onUpload(e.target.files?.[0])}
      />
    </div>
  );
}
